/* eslint prettier/prettier: "off" */

import { css } from 'styled-components';
import THEME, { COLOR } from './colors';

/* Helpers */

export interface ICSSVar {
  name: string;
  value: number | string;
}

// We might receive valid falsly values, so need to do a type check
const isDefined = attr => typeof attr !== 'undefined' && attr !== null;

/* Size & Positioning */

export interface ISizeStyleProps {
  $height?: string;
  $minHeight?: string;
  $maxHeight?: string;
  $width?: string;
  $minWidth?: string;
  $maxWidth?: string;
}

export const sizeStyles = css<ISizeStyleProps>`
  ${props => isDefined(props.$height) && `height: ${props.$height};`}
  ${props => isDefined(props.$minHeight) && `min-height: ${props.$minHeight};`}
  ${props => isDefined(props.$maxHeight) && `max-height: ${props.$maxHeight};`}

  ${props => isDefined(props.$width) && `width: ${props.$width};`}
  ${props => isDefined(props.$minWidth) && `min-width: ${props.$minWidth};`}
  ${props => isDefined(props.$maxWidth) && `max-width: ${props.$maxWidth};`}
`;

export interface IBoxModelStyleProps extends ISizeStyleProps {
  $display?: string;
  $margin?: string;
  $marginTop?: string;
  $marginRight?: string;
  $marginBottom?: string;
  $marginLeft?: string;
  $padding?: string;
  $paddingTop?: string;
  $paddingRight?: string;
  $paddingBottom?: string;
  $paddingLeft?: string;
}

// TODO add borders
export const boxModelStyles = css<IBoxModelStyleProps>`
  ${sizeStyles}

  ${props => isDefined(props.$display) && `display: ${props.$display};`}

  ${props => isDefined(props.$margin) && `margin: ${props.$margin};`}
  ${props => isDefined(props.$marginTop) && `margin-top: ${props.$marginTop};`}
  ${props =>
    isDefined(props.$marginRight) && `margin-right: ${props.$marginRight};`}
  ${props =>
    isDefined(props.$marginBottom) && `margin-bottom: ${props.$marginBottom};`}
  ${props =>
    isDefined(props.$marginLeft) && `margin-left: ${props.$marginLeft};`}
  
  ${props => isDefined(props.$padding) && `padding: ${props.$padding};`}
  ${props =>
    isDefined(props.$paddingTop) && `padding-top: ${props.$paddingTop};`}
  ${props =>
    isDefined(props.$paddingRight) && `padding-right: ${props.$paddingRight};`}
  ${props =>
    isDefined(props.$paddingBottom) &&
    `padding-bottom: ${props.$paddingBottom};`}
  ${props =>
    isDefined(props.$paddingLeft) && `padding-left: ${props.$paddingLeft};`};
`;

export interface IAbsoluteStyleProps {
  $position?: string;
  $zIndex?: string;
  $top?: string;
  $bottom?: string;
  $left?: string;
  $right?: string;
}

export const absoluteStyles = css<IAbsoluteStyleProps>`
  ${props => isDefined(props.$position) && `position: ${props.$position};`}
  ${props => isDefined(props.$zIndex) && `z-index: ${props.$zIndex};`}
  ${props => isDefined(props.$top) && `top: ${props.$top};`}
  ${props => isDefined(props.$bottom) && `bottom: ${props.$bottom};`}
  ${props => isDefined(props.$left) && `left: ${props.$left};`}
  ${props => isDefined(props.$right) && `right: ${props.$right};`};
`;

/* Text */

export interface IFontStyleProps {
  $fontSize?: string;
  $textAlign?: string;
  $lineHeight?: string;
  $fontWeight?: string;
  $textTransform?: string;
  $letterSpacing?: string;
  $textDecoration?: string;
  $whiteSpace?: string;
}

export const fontStyles = css<IFontStyleProps>`
  ${props => isDefined(props.$fontSize) && `font-size: ${props.$fontSize};`}
  ${props => isDefined(props.$textAlign) && `text-align: ${props.$textAlign};`}
  ${props =>
    isDefined(props.$lineHeight) && `line-height: ${props.$lineHeight};`}
  ${props =>
    isDefined(props.$fontWeight) && `font-weight: ${props.$fontWeight};`}
  ${props =>
    isDefined(props.$textTransform) &&
    `text-transform: ${props.$textTransform};`}
  ${props =>
    isDefined(props.$letterSpacing) &&
    `letter-spacing: ${props.$letterSpacing};`}
  ${props =>
    isDefined(props.$textDecoration) &&
    `text-decoration: ${props.$textDecoration};`}
  ${props =>
    isDefined(props.$whiteSpace) && `white-space: ${props.$whiteSpace};`}
`;

/* Color */

const parseColor = (color, defaultColor = THEME.text.primary) => {
  if (!isDefined(color)) return defaultColor;
  if (color[0] === '#') return color;
  if (THEME[color]) return THEME[color];
  return color;
};

export interface IColorStyleProps {
  $color?: string;
  $hoverColor?: string;
}

export const colorStyles = css<IColorStyleProps>`
  ${props => isDefined(props.$color) && `color: ${parseColor(props.$color)};`}
  ${props =>
    isDefined(props.$hoverColor) &&
    css`
      &:hover {
        color: ${parseColor(props.$hoverColor)};
      }
    `};
`;

/* Icons */

const formatIconColorStyles = color =>
  color
    ? `
    color: ${color};
    fill: ${color};
    * {
      color: ${color};
      fill: ${color};
    }
  `
    : '';

const iconColorStyles = css<IColorStyleProps>`
  ${({ $color }) => formatIconColorStyles($color)}
  ${({ $hoverColor }) => `:hover { ${formatIconColorStyles($hoverColor)} }`}
  `;

export interface IIconStyleProps {
  onClick?: any;
  disabled?: boolean;
  $size?: string;
  $height?: string;
  $width?: string;
  $color?: string;
  $overrideColor?: boolean;
  $hoverColor?: string;
}

export const iconStyles = css<IIconStyleProps>`
  cursor: ${props => (props.onClick ? 'pointer' : 'inherit')};

  ${props => props.disabled && 'opacity: 0.5;'}
  ${props => !props.$overrideColor && iconColorStyles}

  ${boxModelStyles}
`;

/* Cards */

export const cardStyles = css`
  background-color: ${COLOR.white};
  border-radius: 8px;
  & .card-border-radius-top {
    border-radius: 8px 8px 0 0;
  }
  & .card-border-radius-bottom {
    border-radius: 0 0 8px 8px;
  }
  ${boxModelStyles}
  ${fontStyles}
  ${colorStyles}
`;

/* Dropdowns */

const DROP_DOWN_BOX_SHADOW = `0 12px 12px 0 ${THEME.shadow.light}`;

export const dropDownBoxShadowStyles = css`
  box-shadow: ${DROP_DOWN_BOX_SHADOW};
`;

export const scrollableDropDownBoxShadowStyles = css`
  box-shadow: ${DROP_DOWN_BOX_SHADOW}, inset 0 -8px 8px -4px ${THEME.shadow.medium};
`;

/* Misc */

// This function can be passed to attrs() as follows:
// const MyStyledComponent = styled.div.attrs(insertClassName('my-class-name'));
// Could be extended to accept an array
export const insertClassName = (className, attrs = {}) => props => ({
  ...props,
  ...attrs,
  className: props.className ? `${props.className} ${className}` : className
});

export const appendClassName = (a: string, b: string | undefined) => b ? `${a} ${b}` : a;